import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { designTemplateById, type DesignCatalogueTemplateId } from './designTemplates';

const BUCKET = 'design-catalogue';

export type DesignCatalogueStatus = 'draft' | 'published' | 'archived';

export type DesignCoverBgMode = 'theme' | 'color' | 'image';

export type DesignCatalogueProject = {
  id: string;
  title: string;
  template_id: DesignCatalogueTemplateId | string;
  status: DesignCatalogueStatus;
  cover_title: string;
  cover_subtitle: string;
  cover_notes: string;
  /** Custom grid columns (1–3). Null = template default. */
  layout_columns: number | null;
  /** Custom grid rows (1–6). Null = template default. */
  layout_rows: number | null;
  cover_bg_mode: DesignCoverBgMode;
  cover_bg_color: string;
  cover_bg_color_to: string;
  cover_bg_image_path: string | null;
  cover_bg_image_url?: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_by_email: string | null;
  updated_by_email: string | null;
  created_at: string;
  updated_at: string;
};

export type DesignCatalogueItem = {
  id: string;
  project_id: string;
  sort_order: number;
  name: string;
  item_code: string;
  packing: string;
  price: string;
  promo_price: string;
  badge: string;
  notes: string;
  category: string;
  image_path: string | null;
  image_url?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type DesignCatalogueDraftItem = Omit<
  DesignCatalogueItem,
  'id' | 'project_id' | 'created_at' | 'updated_at'
> & { id?: string; project_id?: string };

function db() {
  return getDbSupabase('crm');
}

function asProject(row: Record<string, unknown>): DesignCatalogueProject {
  const colsRaw = row.layout_columns;
  const rowsRaw = row.layout_rows;
  const modeRaw = String(row.cover_bg_mode ?? 'theme');
  const mode: DesignCoverBgMode =
    modeRaw === 'color' || modeRaw === 'image' ? modeRaw : 'theme';
  return {
    id: String(row.id),
    title: String(row.title ?? 'Untitled catalogue'),
    template_id: String(row.template_id ?? 'promo_grid_clearance'),
    status: (String(row.status ?? 'draft') as DesignCatalogueStatus) || 'draft',
    cover_title: String(row.cover_title ?? ''),
    cover_subtitle: String(row.cover_subtitle ?? ''),
    cover_notes: String(row.cover_notes ?? ''),
    layout_columns:
      colsRaw === null || colsRaw === undefined || colsRaw === ''
        ? null
        : Number(colsRaw),
    layout_rows:
      rowsRaw === null || rowsRaw === undefined || rowsRaw === ''
        ? null
        : Number(rowsRaw),
    cover_bg_mode: mode,
    cover_bg_color: String(row.cover_bg_color ?? ''),
    cover_bg_color_to: String(row.cover_bg_color_to ?? ''),
    cover_bg_image_path: row.cover_bg_image_path ? String(row.cover_bg_image_path) : null,
    created_by: row.created_by ? String(row.created_by) : null,
    updated_by: row.updated_by ? String(row.updated_by) : null,
    created_by_email: row.created_by_email ? String(row.created_by_email) : null,
    updated_by_email: row.updated_by_email ? String(row.updated_by_email) : null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

function asItem(row: Record<string, unknown>): DesignCatalogueItem {
  return {
    id: String(row.id),
    project_id: String(row.project_id),
    sort_order: Number(row.sort_order ?? 0),
    name: String(row.name ?? ''),
    item_code: String(row.item_code ?? ''),
    packing: String(row.packing ?? ''),
    price: String(row.price ?? ''),
    promo_price: String(row.promo_price ?? ''),
    badge: String(row.badge ?? ''),
    notes: String(row.notes ?? ''),
    category: String(row.category ?? ''),
    image_path: row.image_path ? String(row.image_path) : null,
  };
}

export async function listDesignCatalogueProjects(
  options: { status?: DesignCatalogueStatus | 'active' } = {},
): Promise<DesignCatalogueProject[]> {
  let query = db().from('design_catalogue_projects').select('*');
  if (options.status === 'archived') {
    query = query.eq('status', 'archived');
  } else if (options.status === 'published') {
    query = query.eq('status', 'published');
  } else if (options.status === 'draft') {
    query = query.eq('status', 'draft');
  } else {
    // active = not archived
    query = query.neq('status', 'archived');
  }
  const { data, error } = await query.order('updated_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => asProject(row as Record<string, unknown>));
}

export async function fetchDesignCatalogueProject(
  id: string,
): Promise<{ project: DesignCatalogueProject; items: DesignCatalogueItem[] }> {
  const { data: projectRow, error: projectError } = await db()
    .from('design_catalogue_projects')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (projectError) throw new Error(formatSupabaseError(projectError));
  if (!projectRow) throw new Error('Catalogue not found.');

  const { data: itemRows, error: itemsError } = await db()
    .from('design_catalogue_items')
    .select('*')
    .eq('project_id', id)
    .order('sort_order', { ascending: true });
  if (itemsError) throw new Error(formatSupabaseError(itemsError));

  const project = asProject(projectRow as Record<string, unknown>);
  if (project.cover_bg_image_path) {
    project.cover_bg_image_url = await signedDesignCatalogueImageUrl(project.cover_bg_image_path);
  }

  const items = await attachSignedUrls(
    (itemRows ?? []).map((row) => asItem(row as Record<string, unknown>)),
  );
  return { project, items };
}

async function attachSignedUrls(items: DesignCatalogueItem[]): Promise<DesignCatalogueItem[]> {
  const paths = items.map((i) => i.image_path).filter((p): p is string => Boolean(p));
  if (paths.length === 0) return items.map((i) => ({ ...i, image_url: null }));

  const { data, error } = await db().storage.from(BUCKET).createSignedUrls(paths, 60 * 60);
  if (error) {
    // Still return items without URLs rather than failing the whole load.
    return items.map((i) => ({ ...i, image_url: null }));
  }
  const byPath = new Map<string, string>();
  for (const row of data ?? []) {
    if (row.path && row.signedUrl) byPath.set(row.path, row.signedUrl);
  }
  return items.map((i) => ({
    ...i,
    image_url: i.image_path ? byPath.get(i.image_path) ?? null : null,
  }));
}

export type SaveDesignCatalogueInput = {
  id?: string;
  /** When updating, pass last known updated_at for conflict detection. */
  expectedUpdatedAt?: string | null;
  title: string;
  template_id: string;
  status: DesignCatalogueStatus;
  cover_title: string;
  cover_subtitle: string;
  cover_notes: string;
  layout_columns?: number | null;
  layout_rows?: number | null;
  cover_bg_mode?: DesignCoverBgMode;
  cover_bg_color?: string;
  cover_bg_color_to?: string;
  cover_bg_image_path?: string | null;
  items: DesignCatalogueDraftItem[];
  actorEmail?: string | null;
  actorId?: string | null;
};

export type SaveDesignCatalogueResult = {
  project: DesignCatalogueProject;
  items: DesignCatalogueItem[];
  conflict: boolean;
  conflictMessage?: string;
};

export async function saveDesignCatalogue(
  input: SaveDesignCatalogueInput,
): Promise<SaveDesignCatalogueResult> {
  const template = designTemplateById(input.template_id);
  const nowIso = new Date().toISOString();
  const email = input.actorEmail?.trim() || null;
  const actorId = input.actorId?.trim() || null;

  if (input.id) {
    const { data: existing, error: existingError } = await db()
      .from('design_catalogue_projects')
      .select('updated_at, updated_by_email')
      .eq('id', input.id)
      .maybeSingle();
    if (existingError) throw new Error(formatSupabaseError(existingError));
    if (!existing) throw new Error('Catalogue not found.');

    const remoteUpdatedAt = String((existing as { updated_at?: string }).updated_at ?? '');
    const expected = input.expectedUpdatedAt?.trim() || '';
    let conflict = false;
    let conflictMessage: string | undefined;
    if (expected && remoteUpdatedAt && expected !== remoteUpdatedAt) {
      conflict = true;
      const who = String((existing as { updated_by_email?: string | null }).updated_by_email ?? '').trim();
      conflictMessage = who
        ? `Someone else (${who}) saved this catalogue since you opened it. Your save overwrote their version.`
        : 'Someone else saved this catalogue since you opened it. Your save overwrote their version.';
    }

    const { error: projectError } = await db()
      .from('design_catalogue_projects')
      .update({
        title: input.title.trim() || 'Untitled catalogue',
        template_id: template.id,
        status: input.status,
        cover_title: input.cover_title,
        cover_subtitle: input.cover_subtitle,
        cover_notes: input.cover_notes,
        layout_columns: input.layout_columns ?? null,
        layout_rows: input.layout_rows ?? null,
        cover_bg_mode: input.cover_bg_mode ?? 'theme',
        cover_bg_color: input.cover_bg_color ?? '',
        cover_bg_color_to: input.cover_bg_color_to ?? '',
        cover_bg_image_path: input.cover_bg_image_path ?? null,
        updated_by: actorId,
        updated_by_email: email,
        updated_at: nowIso,
      })
      .eq('id', input.id);
    if (projectError) throw new Error(formatSupabaseError(projectError));

    await replaceProjectItems(input.id, input.items);
    const loaded = await fetchDesignCatalogueProject(input.id);
    return {
      project: loaded.project,
      items: loaded.items,
      conflict,
      conflictMessage,
    };
  }

  const { data: projectRow, error: projectError } = await db()
    .from('design_catalogue_projects')
    .insert({
      title: input.title.trim() || 'Untitled catalogue',
      template_id: template.id,
      status: input.status,
      cover_title: input.cover_title,
      cover_subtitle: input.cover_subtitle,
      cover_notes: input.cover_notes,
      layout_columns: input.layout_columns ?? null,
      layout_rows: input.layout_rows ?? null,
      cover_bg_mode: input.cover_bg_mode ?? 'theme',
      cover_bg_color: input.cover_bg_color ?? '',
      cover_bg_color_to: input.cover_bg_color_to ?? '',
      cover_bg_image_path: input.cover_bg_image_path ?? null,
      created_by: actorId,
      updated_by: actorId,
      created_by_email: email,
      updated_by_email: email,
      created_at: nowIso,
      updated_at: nowIso,
    })
    .select('*')
    .single();
  if (projectError) throw new Error(formatSupabaseError(projectError));

  const project = asProject(projectRow as Record<string, unknown>);
  await replaceProjectItems(project.id, input.items);
  const loaded = await fetchDesignCatalogueProject(project.id);
  return { project: loaded.project, items: loaded.items, conflict: false };
}

async function replaceProjectItems(
  projectId: string,
  items: DesignCatalogueDraftItem[],
): Promise<void> {
  const { error: deleteError } = await db()
    .from('design_catalogue_items')
    .delete()
    .eq('project_id', projectId);
  if (deleteError) throw new Error(formatSupabaseError(deleteError));

  const rows = items.map((item, index) => ({
    project_id: projectId,
    sort_order: index,
    name: item.name ?? '',
    item_code: item.item_code ?? '',
    packing: item.packing ?? '',
    price: item.price ?? '',
    promo_price: item.promo_price ?? '',
    badge: item.badge ?? '',
    notes: item.notes ?? '',
    category: item.category ?? '',
    image_path: item.image_path ?? null,
  }));
  if (rows.length === 0) return;

  const { error: insertError } = await db().from('design_catalogue_items').insert(rows);
  if (insertError) throw new Error(formatSupabaseError(insertError));
}

export async function archiveDesignCatalogue(id: string): Promise<void> {
  await setDesignCatalogueStatus(id, 'archived');
}

export async function setDesignCatalogueStatus(
  id: string,
  status: DesignCatalogueStatus,
  actor?: { email?: string | null; id?: string | null },
): Promise<void> {
  const { error } = await db()
    .from('design_catalogue_projects')
    .update({
      status,
      updated_at: new Date().toISOString(),
      ...(actor?.id ? { updated_by: actor.id } : {}),
      ...(actor?.email ? { updated_by_email: actor.email } : {}),
    })
    .eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

/** Copy project + items (image paths shared). Returns the new project id. */
export async function duplicateDesignCatalogue(
  sourceId: string,
  actor?: { email?: string | null; id?: string | null },
): Promise<DesignCatalogueProject> {
  const { project, items } = await fetchDesignCatalogueProject(sourceId);
  const nowIso = new Date().toISOString();
  const email = actor?.email?.trim() || null;
  const actorId = actor?.id?.trim() || null;

  const { data: projectRow, error: projectError } = await db()
    .from('design_catalogue_projects')
    .insert({
      title: `${project.title.replace(/\s*\(copy\)\s*$/i, '').trim() || 'Untitled catalogue'} (copy)`,
      template_id: project.template_id,
      status: 'draft',
      cover_title: project.cover_title,
      cover_subtitle: project.cover_subtitle,
      cover_notes: project.cover_notes,
      layout_columns: project.layout_columns,
      layout_rows: project.layout_rows,
      cover_bg_mode: project.cover_bg_mode,
      cover_bg_color: project.cover_bg_color,
      cover_bg_color_to: project.cover_bg_color_to,
      cover_bg_image_path: project.cover_bg_image_path,
      created_by: actorId,
      updated_by: actorId,
      created_by_email: email,
      updated_by_email: email,
      created_at: nowIso,
      updated_at: nowIso,
    })
    .select('*')
    .single();
  if (projectError) throw new Error(formatSupabaseError(projectError));

  const newProject = asProject(projectRow as Record<string, unknown>);
  await replaceProjectItems(
    newProject.id,
    items.map((item) => ({
      sort_order: item.sort_order,
      name: item.name,
      item_code: item.item_code,
      packing: item.packing,
      price: item.price,
      promo_price: item.promo_price,
      badge: item.badge,
      notes: item.notes,
      category: item.category,
      image_path: item.image_path,
    })),
  );
  return newProject;
}

export type DesignCatalogueEditor = {
  auth_id: string;
  email: string | null;
  display_name: string | null;
  last_seen_at: string;
};

const PRESENCE_STALE_MS = 45_000;

export async function pingDesignCataloguePresence(input: {
  projectId: string;
  authId: string;
  email?: string | null;
  displayName?: string | null;
}): Promise<void> {
  const { error } = await db().from('design_catalogue_presence').upsert(
    {
      project_id: input.projectId,
      auth_id: input.authId,
      email: input.email?.trim() || null,
      display_name: input.displayName?.trim() || null,
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: 'project_id,auth_id' },
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function clearDesignCataloguePresence(
  projectId: string,
  authId: string,
): Promise<void> {
  const { error } = await db()
    .from('design_catalogue_presence')
    .delete()
    .eq('project_id', projectId)
    .eq('auth_id', authId);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function listDesignCatalogueEditors(
  projectId: string,
): Promise<DesignCatalogueEditor[]> {
  const cutoff = new Date(Date.now() - PRESENCE_STALE_MS).toISOString();
  const { data, error } = await db()
    .from('design_catalogue_presence')
    .select('auth_id, email, display_name, last_seen_at')
    .eq('project_id', projectId)
    .gte('last_seen_at', cutoff)
    .order('last_seen_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => ({
    auth_id: String((row as { auth_id: string }).auth_id),
    email: (row as { email?: string | null }).email
      ? String((row as { email: string }).email)
      : null,
    display_name: (row as { display_name?: string | null }).display_name
      ? String((row as { display_name: string }).display_name)
      : null,
    last_seen_at: String((row as { last_seen_at: string }).last_seen_at ?? ''),
  }));
}

export async function uploadDesignCatalogueImage(
  projectId: string,
  file: File,
): Promise<string> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${projectId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await db().storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return path;
}

export async function signedDesignCatalogueImageUrl(path: string): Promise<string | null> {
  const { data, error } = await db().storage.from(BUCKET).createSignedUrl(path, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export function emptyDesignCatalogueItem(sortOrder = 0): DesignCatalogueDraftItem {
  return {
    sort_order: sortOrder,
    name: '',
    item_code: '',
    packing: '',
    price: '',
    promo_price: '',
    badge: '',
    notes: '',
    category: '',
    image_path: null,
    image_url: null,
  };
}

export function chunkCatalogueItems<T>(items: T[], perPage: number): T[][] {
  const size = Math.max(1, perPage);
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  if (pages.length === 0) pages.push([]);
  return pages;
}
